import moment from "moment-timezone";
import React from "react";

import { ICON_SIZES, ICON_TYPES } from "../../../constants/icons";
import { MemberProfileActivityEntry, ShieldIcon } from "../../../component-library";

const formatCallDate = (call_date, call_time, format) => {
  const localCallDatetime = moment.utc(`${call_date} ${call_time}`).local();

  return localCallDatetime.format(format);
};

export const CallScheduledActivity = (props) => {
  const { keyContent, localCreatedTime } = props;
  const { appointment_created_by, call_date, call_time, provider_name } = keyContent;

  return (
    <MemberProfileActivityEntry
      headerClassName="apti-MemberProfileActivityEntry__Header__Dark"
      iconAlt="CallScheduledIcon"
      iconSrc="icons/call-icon-dark.svg"
      localDateTime={localCreatedTime}
      subTitle={`Appointment Scheduled for 
                ${formatCallDate(call_date, call_time, "MM/DD/YYYY \\at\\ hh:mm a")} 
                with ${provider_name}`}
      title="Video Call Scheduled"
    >
      <div className="fs-14">{`Created by ${appointment_created_by}`}</div>
    </MemberProfileActivityEntry>
  );
};

export const CallCompletedActivity = (props) => {
  const { keyContent, localCreatedTime } = props;
  const { appointment_created_by, call_date, call_time, provider_name } = keyContent;

  return (
    <MemberProfileActivityEntry
      headerClassName="apti-MemberProfileActivityEntry__Header__Primary"
      iconAlt="CallCompletedIcon"
      iconSrc="icons/call-icon-primary.svg"
      localDateTime={localCreatedTime}
      subTitle={`Appointment Completed for 
                ${formatCallDate(call_date, call_time, "MM/DD/YYYY \\at\\ hh:mm a")} 
                with ${provider_name}`}
      title="Video Call Completed"
    >
      <div className="fs-14">{`Created by ${appointment_created_by}`}</div>
    </MemberProfileActivityEntry>
  );
};

const buildCallCancelledActivitySubTitle = (
  is_auto_cancelled,
  call_date,
  call_time,
  provider_name,
) => {
  if (is_auto_cancelled) {
    return "Video Call Auto-Cancelled";
  }

  return `Appointment Cancelled for 
        ${formatCallDate(call_date, call_time, "MM/DD/YYYY \\at\\ hh:mm a")} 
        with ${provider_name}`;
};

const getCancelledBy = (
  cancellation_reason,
  provider_name,
  patient_name,
  session_cancelled_by,
  provider_id,
  patient_id,
) => {
  if (
    cancellation_reason &&
    cancellation_reason.cancelled_by_user_type &&
    cancellation_reason.cancelled_by_user_type.includes("admin")
  ) {
    return "Admin";
  }
  if (session_cancelled_by === patient_id && patient_name) {
    return patient_name;
  } else if (session_cancelled_by === provider_id && provider_name) {
    return provider_name;
  } else {
    return provider_name;
  }
};

const buildCallCancelledActivityContent = (
  is_auto_cancelled,
  call_date,
  call_time,
  provider_name,
  cancellation_reason,
  patient_name,
  session_cancelled_by,
  provider_id,
  patient_id,
) => {
  const formattedDate = formatCallDate(call_date, call_time, "MM/DD/YYYY at hh:mm a");

  if (is_auto_cancelled) {
    return `Session originally scheduled for ${formattedDate} has been auto-cancelled.`;
  }

  return `Cancelled by ${getCancelledBy(
    cancellation_reason,
    provider_name,
    patient_name,
    session_cancelled_by,
    provider_id,
    patient_id,
  )}`;
};

export const CallCancelledActivity = (props) => {
  const { keyContent, localCreatedTime } = props;
  const {
    call_date,
    call_time,
    cancellation_reason,
    is_auto_cancelled,
    patient_id,
    patient_name,
    provider_id,
    provider_name,
    session_cancelled_by,
  } = keyContent;

  return (
    <MemberProfileActivityEntry
      headerClassName="apti-MemberProfileActivityEntry__Header__Danger"
      iconAlt="CallCancelledIcon"
      iconSrc="icons/call-icon-danger.svg"
      localDateTime={localCreatedTime}
      subTitle={buildCallCancelledActivitySubTitle(
        is_auto_cancelled,
        call_date,
        call_time,
        provider_name,
      )}
      title="Video Call Cancelled"
    >
      <div className="fs-14">
        {buildCallCancelledActivityContent(
          is_auto_cancelled,
          call_date,
          call_time,
          provider_name,
          cancellation_reason,
          patient_name,
          session_cancelled_by,
          provider_id,
          patient_id,
        )}
      </div>
    </MemberProfileActivityEntry>
  );
};

export const CallRescheduledActivity = (props) => {
  const { keyContent, localCreatedTime } = props;
  const { appointment_created_by, call_date, call_time, provider_name } = keyContent;

  return (
    <MemberProfileActivityEntry
      headerClassName="apti-MemberProfileActivityEntry__Header__Dark"
      iconAlt="CallRescheduledIcon"
      iconSrc="icons/call-icon-dark.svg"
      localDateTime={localCreatedTime}
      subTitle={`Appointment Rescheduled for 
                ${formatCallDate(call_date, call_time, "MM/DD/YYYY \\at\\ hh:mm a")} 
                with ${provider_name}`}
      title="Video Call Rescheduled"
    >
      <div className="fs-14">{`Rescheduled by ${appointment_created_by}`}</div>
    </MemberProfileActivityEntry>
  );
};

export const InstantCallRequestedActivity = (props) => {
  const { keyContent, localCreatedTime } = props;
  const { appointment_created_by, call_date, call_time, provider_name } = keyContent;

  return (
    <MemberProfileActivityEntry
      children={<div className="fs-14">{`Created by ${appointment_created_by}`}</div>}
      headerClassName="apti-MemberProfileActivityEntry__Header__Warning"
      iconAlt="CallStartedIcon"
      iconSrc="icons/call-icon-warning.svg"
      localDateTime={localCreatedTime}
      subTitle={`Call Started for 
                ${formatCallDate(call_date, call_time, "MM/DD/YYYY \\at\\ hh:mm a")} 
                with ${provider_name}`}
      title="Video Call Requested"
    />
  );
};

export const PatientNoShowActivity = (props) => {
  const { keyContent, localCreatedTime } = props;
  const { call_date, call_time, provider_name } = keyContent;

  return (
    <MemberProfileActivityEntry
      headerClassName="apti-MemberProfileActivityEntry__Header__Danger"
      iconAlt="PatientNoShowIcon"
      iconSrc="icons/call-icon-danger.svg"
      localDateTime={localCreatedTime}
      subTitle={`No-Showed for 
                ${formatCallDate(call_date, call_time, "MM/DD/YYYY hh:mm a")} 
                appointment with ${provider_name}`}
      title="Patient No-Show"
    />
  );
};

export const SchedulingRestrictionActivity = (props) => {
  const { keyContent, localCreatedTime } = props;
  const { is_scheduling_restricted, updated_by_name } = keyContent;

  const subtitle = is_scheduling_restricted
    ? "Patient Restricted from Scheduling"
    : "Patient Scheduling Restriction Removed";

  return (
    <MemberProfileActivityEntry
      headerClassName="apti-MemberProfileActivityEntry__Header__Dark"
      iconComponent={
        <ShieldIcon
          className="apti-MemberProfileActivityEntry__ContentContainer__Icon"
          iconSize={ICON_SIZES.LARGE}
          iconType={ICON_TYPES.CIRCLE}
          style={{ fill: "#103E50" }}
        />
      }
      localDateTime={localCreatedTime}
      subTitle={subtitle}
      title="Scheduling Restriction Change"
    >
      <div className="fs-14">{`Updated by ${updated_by_name}`}</div>
    </MemberProfileActivityEntry>
  );
};
